import {
  AvatarGroup,
  ChipCounterList,
  Column,
  OverflowTooltip,
} from '../../../components';
import dateRenderer from '../../../components/cellRenderers/dateRenderer';
import execTimeRenderer from '../../../components/cellRenderers/execTimeRenderer';
import testNameRenderer from '../../../components/cellRenderers/testNameRenderer';
import testTypeRenderer from '../../../components/cellRenderers/testTypeRenderer';
import failRateRenderer from '../../../components/cellRenderers/failRateRenderer';
import resultsRenderer from '../../../components/cellRenderers/resultsRenderer';
import dateComparer from '../../../utils/comparers/dateComparer';
import numericComparer from '../../../utils/comparers/numericComparer';
import failRateComparer from '../../../utils/comparers/failRateComparer';
import resultsComparer from '../../../utils/comparers/resultsComparer';
import { distinctArray, isInSaaSMode } from '../../../utils/common';
import { getFilterOptionsOverride } from '../../../utils/graphqlFilters';
import { ColumnFilterOption } from '../../../components/Table/types';
import { TestRun } from '../../../types';

const getItemUniqueFilterOptions = (
  tableData: TestRun[],
  fieldPath: keyof NonNullable<TestRun['asset_metadata']>,
) => {
  const allItems =
    tableData
      ?.flatMap((item) => item?.asset_metadata?.[fieldPath] || '')
      .filter((val) => val !== '') || [];
  const distinctItems = distinctArray(allItems as string[]);
  return distinctItems.map((item) => ({ value: item, label: item }));
};

export const getTestRunsTableColumns = (
  tableData: TestRun[],
  filterOptionsOverride: Record<string, ColumnFilterOption[]> | null = null,
): Array<Column<TestRun>> => {
  const isSaaS = isInSaaSMode();

  const getFilterOptions = (
    columnId: string,
    defaultOptions?: ColumnFilterOption[],
  ) =>
    getFilterOptionsOverride(columnId, filterOptionsOverride, defaultOptions);

  const maxExecTime = tableData
    ? Math.max(...tableData.map((item) => Number(item.last_exec_time) || 0))
    : 0;

  const maxMedExecTime = tableData
    ? Math.max(...tableData.map((item) => Number(item.median_exec_time) || 0))
    : 0;

  const baseColumns: Array<Column<TestRun>> = [
    {
      id: 'metadata.database_name',
      header: 'Database',
      defaultUntoggled: true,
      filterOptionsOverride: getFilterOptions('metadata.database_name'),
    },
    {
      id: 'metadata.schema_name',
      header: 'Schema',
      defaultUntoggled: true,
      filterOptionsOverride: getFilterOptions('metadata.schema_name'),
    },
    {
      id: 'metadata.table_name',
      header: 'Table name',
      filterDropdownWidth: 350,
      filterOptionsOverride: getFilterOptions('metadata.table_name'),
    },
    {
      id: 'metadata.column_name',
      header: 'Column name',
      filterDropdownWidth: 350,
      filterOptionsOverride: getFilterOptions('metadata.column_name'),
    },
    {
      id: 'metadata.test_name',
      header: 'Test name',
      renderer: testNameRenderer,
      nonToggleable: true,
      filterDropdownWidth: 400,
      filterOptionsOverride: getFilterOptions('metadata.test_name'),
    },
    {
      id: 'metadata.test_sub_type',
      header: 'Test type',
      renderer: testTypeRenderer,
      filterOptionsOverride: getFilterOptions('metadata.test_sub_type'),
    },
    {
      id: 'metadata.test_tags',
      header: 'Test tags',
      renderer: (val) => (
        <OverflowTooltip>{val.metadata.test_tags?.join(', ')}</OverflowTooltip>
      ),
      filterOperators: ['equals', 'notEquals'],
      filterOptionsOverride: getFilterOptions(
        'metadata.test_tags',
        distinctArray(
          tableData.map((item) => item.metadata.test_tags || ['']).flat(),
        ).map((item) => ({ value: item, label: item })),
      ),
      defaultUntoggled: true,
    },
    {
      id: 'last_exec_time',
      header: 'Last exec. time',
      filterable: false,
      renderer: (rowData) =>
        execTimeRenderer(rowData.last_exec_time, maxExecTime),
      compareFunc: (a, b) => numericComparer(a, b, 'last_exec_time'),
      defaultUntoggled: true,
    },
    {
      id: 'median_exec_time',
      header: 'Median exec. time',
      filterable: false,
      // Median is a windowed aggregate with no server sort field, so it can't
      // be sorted in the SaaS (server-controlled) table.
      sortable: !isSaaS,
      renderer: (rowData) =>
        execTimeRenderer(rowData.median_exec_time, maxMedExecTime),
      compareFunc: (a, b) => numericComparer(a, b, 'median_exec_time'),
    },
    {
      id: 'metadata.latest_run_time',
      header: 'Last test run',
      compareFunc: (a, b) =>
        dateComparer(a.metadata.latest_run_time, b.metadata.latest_run_time),
      filterable: false,
      renderer: (val) => dateRenderer(val.metadata.latest_run_time),
      defaultUntoggled: true,
    },
    {
      id: 'test_runs.fail_rate',
      header: 'Fail Rate',
      renderer: failRateRenderer,
      compareFunc: failRateComparer,
      // Column shows fail rate, but the only server sort field is failure count
      // (FAILED_ROWS), so sorting is disabled in the SaaS (server-controlled)
      // table to avoid the rate/count mismatch. OSS still sorts client-side by
      // the actual fail-rate value.
      sortable: !isSaaS,
      filterable: false,
    },
    {
      id: 'test_runs.totals',
      header: 'Results',
      renderer: resultsRenderer,
      compareFunc: resultsComparer,
      // Aggregated run totals have no server sort field, so not sortable in the
      // SaaS (server-controlled) table.
      sortable: !isSaaS,
      nonToggleable: true,
      width: 140,
      filterOperators: ['equals', 'notEquals'],
      filterOptionsOverride: [
        { value: 'passed', label: 'Passed' },
        { value: 'warnings', label: 'Warnings' },
        { value: 'errors', label: 'Errors' },
        { value: 'failures', label: 'Failures' },
      ],
      filterValueMatcher: (selectedFilterValue, rowData, operator) => {
        const totals = rowData.test_runs?.totals;
        if (!totals) return false;

        const hasMatchingStatus = selectedFilterValue.some((filterValue) => {
          switch (filterValue) {
            case 'passed':
              return totals.passed >= 1;
            case 'warnings':
              return totals.warnings >= 1;
            case 'errors':
              return totals.errors >= 1;
            case 'failures':
              return totals.failures >= 1;
            default:
              return false;
          }
        });

        return operator === 'equals' ? hasMatchingStatus : !hasMatchingStatus;
      },
    },
    {
      id: 'asset_metadata.tags',
      header: 'Asset Tags',
      defaultUntoggled: true,
      filterOperators: ['equals', 'notEquals'],
      emptyValueLabel: 'No asset tags',
      filterOptionsOverride: getFilterOptions(
        'asset_metadata.tags',
        getItemUniqueFilterOptions(tableData, 'tags'),
      ),
      renderer: ({ asset_metadata }) => (
        <ChipCounterList items={asset_metadata?.tags} />
      ),
    },
    {
      id: 'asset_metadata.owners',
      header: 'Asset Owners',
      defaultUntoggled: true,
      filterOperators: ['equals', 'notEquals'],
      width: 200,
      emptyValueLabel: 'No asset owners',
      filterOptionsOverride: getFilterOptions(
        'asset_metadata.owners',
        getItemUniqueFilterOptions(tableData, 'owners'),
      ),
      renderer: ({ asset_metadata }) => (
        <AvatarGroup items={asset_metadata?.owners} />
      ),
    },
  ];

  if (isSaaS) {
    const assetColumns: Array<Column<TestRun>> = [
      {
        id: 'asset_metadata.name',
        header: 'Asset Name',
        defaultUntoggled: true,
        filterOptionsOverride: getFilterOptions('asset_metadata.name'),
      },
      {
        id: 'asset_metadata.is_critical',
        header: 'Critical Asset',
        defaultUntoggled: true,
        renderer: ({ asset_metadata }) => (
          <>{asset_metadata?.is_critical ? 'Yes' : 'No'}</>
        ),
      },
      {
        id: 'asset_metadata.subscribers',
        header: 'Asset Subscribers',
        filterOperators: ['equals', 'notEquals'],
        emptyValueLabel: 'No asset subscribers',
        filterOptionsOverride: getFilterOptions(
          'asset_metadata.subscribers',
          getItemUniqueFilterOptions(tableData, 'subscribers'),
        ),
        defaultUntoggled: true,
        width: 200,
        renderer: ({ asset_metadata }) => (
          <AvatarGroup items={asset_metadata?.subscribers} />
        ),
      },
      {
        id: 'asset_metadata.path',
        header: 'Asset Path',
        defaultUntoggled: true,
        hideColumnFilter: true,
        filterOperators: ['contains', 'notContains'],
      },
    ];

    return [...baseColumns, ...assetColumns];
  }

  return baseColumns;
};
